import { useEffect, useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { Panel, PanelHeader } from "@/components/common/Card";
import { getSettings, updateSettings, testTelegram } from "@/lib/api";
import { SETTINGS } from "@/constants/testIds";
import {
  Gear,
  Key,
  PaperPlaneTilt,
  ShieldCheck,
  Pencil,
  X,
  FloppyDisk,
  Lightning,
  Funnel,
  TrendUp,
  Wallet,
  Warning,
} from "@phosphor-icons/react";

const MODES = [
  { v: "conservative", l: "Conservative" },
  { v: "balanced", l: "Balanced" },
  { v: "aggressive", l: "Aggressive" },
];

const STRATEGIES = [
  { v: "trend_pullback", l: "Trend Pullback" },
  { v: "breakout_retest", l: "Breakout Retest" },
];

// Gated strategies — controlled by their own feature flag (single source of
// truth), NOT the enabled_strategies checkbox list. Both default OFF.
const GATED_STRATEGIES = [
  {
    key: "enable_range_trading",
    label: "Enable Range Trading",
    note: "Off by default (0% historical win rate). Only fires in the RANGING regime when on.",
    testid: "settings-enable-range-trading",
  },
  {
    key: "enable_liquidity_sweep",
    label: "Enable Liquidity Sweep",
    note: "Off by default (Jun 2026 audit: net-negative in both directions). Turning on lets the strategy emit signals again.",
    testid: "settings-enable-liquidity-sweep",
  },
  {
    key: "enable_ma_crossover",
    label: "Enable MA Crossover",
    note: "Off by default — no track record yet. EMA10x30 golden/death cross on 1H, confirmed by ribbon alignment + 15m rejection.",
    testid: "settings-enable-ma-crossover",
  },
  {
    key: "enable_divergence",
    label: "Enable Divergence",
    note: "Off by default — no track record yet. 1H price vs RSI/MACD divergence at the current swing extreme.",
    testid: "settings-enable-divergence",
  },
  {
    key: "enable_fibonacci_retracement",
    label: "Enable Fibonacci Retracement",
    note: "Off by default — no track record yet. Pullback into the 38.2-61.8% zone of the latest 1H swing leg, aligned with 4H trend.",
    testid: "settings-enable-fibonacci-retracement",
  },
  {
    key: "enable_ichimoku",
    label: "Enable Ichimoku Cloud",
    note: "Off by default — no track record yet. Price above/below the 1H cloud with a Tenkan/Kijun cross.",
    testid: "settings-enable-ichimoku",
  },
  {
    key: "enable_volume_profile",
    label: "Enable Volume Profile",
    note: "Off by default — no track record yet. Rejection off the value-area high/low of the 1H volume profile.",
    testid: "settings-enable-volume-profile",
  },
  {
    key: "enable_chart_patterns",
    label: "Enable Chart Patterns",
    note: "Off by default — no track record yet. Double Top/Bottom and Head-and-Shoulders from 1H swing structure.",
    testid: "settings-enable-chart-patterns",
  },
  {
    key: "enable_momentum_rotation",
    label: "Enable Momentum Rotation",
    note: "Off by default — no track record yet. Cross-pair ranking: rotates into the strongest 1H rate-of-change movers.",
    testid: "settings-enable-momentum-rotation",
  },
];

// Locked product defaults (Feb 2026) — used as input placeholders and the
// fallback the backend applies when the value is submitted as null/blank.
const FILTER_DEFAULTS = {
  funding_soft_threshold: 0.0005,
  funding_hard_threshold: 0.003,
  oi_soft_pct: 10.0,
  oi_hard_pct: 30.0,
  trending_up_long_min_adx: 55,
  trend_pullback_min_volume_ratio: 1.5,
};

const mask = (val) => {
  if (!val) return "—";
  const s = String(val);
  if (s.length <= 4) return "••••";
  return `••••••••${s.slice(-4)}`;
};

// Blank / empty -> null so the backend keeps its locked default.
const blankToNull = (v) =>
  v === "" || v === null || v === undefined || Number.isNaN(Number(v))
    ? null
    : Number(v);

const Label = ({ children, hint }) => (
  <div className="mb-1.5">
    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
      {children}
    </div>
    {hint && <div className="text-[11px] text-slate-400 mt-0.5">{hint}</div>}
  </div>
);

const TextInput = ({ value, onChange, placeholder, testid, type = "text" }) => (
  <input
    data-testid={testid}
    type={type}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full bg-[#f1f5f9] border border-slate-200 hover:border-slate-300 text-slate-900 text-sm font-data rounded-sm px-3 py-2 focus:border-[#2563eb] focus:outline-none"
  />
);

const NumberInput = ({ value, onChange, testid, min, max, step, placeholder }) => (
  <input
    data-testid={testid}
    type="number"
    min={min}
    max={max}
    step={step}
    placeholder={placeholder}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
    className="w-full bg-[#f1f5f9] border border-slate-200 hover:border-slate-300 text-slate-900 text-sm font-data rounded-sm px-3 py-2 focus:border-[#2563eb] focus:outline-none"
  />
);

const Select = ({ value, onChange, options, testid }) => (
  <select
    data-testid={testid}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full bg-[#f1f5f9] border border-slate-200 hover:border-slate-300 text-slate-900 text-sm font-data rounded-sm px-3 py-2 focus:border-[#2563eb] focus:outline-none"
  >
    {options.map((o) => (
      <option key={o.v} value={o.v}>
        {o.l}
      </option>
    ))}
  </select>
);

const SecretField = ({ savedValue, draftValue, onChangeDraft, editing, testid, placeholder }) => {
  if (!editing) {
    return (
      <div
        data-testid={`${testid}-masked`}
        className="bg-[#f1f5f9] border border-slate-200/70 text-slate-700 text-sm font-data rounded-sm px-3 py-2 select-none"
      >
        {mask(savedValue)}
      </div>
    );
  }
  return (
    <TextInput
      testid={testid}
      value={draftValue}
      onChange={onChangeDraft}
      placeholder={placeholder}
    />
  );
};

const SectionHeader = ({ icon: Icon, title, hint, action }) => (
  <PanelHeader
    title={
      <span className="inline-flex items-center gap-2">
        <Icon size={12} className="text-[#2563eb]" weight="bold" />
        {title}
      </span>
    }
    hint={hint}
    action={action}
  />
);

const EditToggle = ({ editing, onToggle, onCancel, testidEdit, testidCancel }) =>
  editing ? (
    <button
      data-testid={testidCancel}
      onClick={onCancel}
      className="inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-300 px-2.5 py-1 rounded-sm transition"
    >
      <X size={11} weight="bold" />
      Cancel
    </button>
  ) : (
    <button
      data-testid={testidEdit}
      onClick={onToggle}
      className="inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase text-slate-600 hover:text-[#2563eb] border border-slate-200 hover:border-[#2563eb]/40 px-2.5 py-1 rounded-sm transition"
    >
      <Pencil size={11} weight="bold" />
      Edit
    </button>
  );

export const Settings = () => {
  const { data: saved, mutate } = useSWR("/settings", getSettings);

  // Drafts for non-secret fields
  const [draft, setDraft] = useState({});
  // Edit toggles for secret blocks
  const [editWeex, setEditWeex] = useState(false);
  const [editTelegram, setEditTelegram] = useState(false);

  // Secret drafts (only used while editing)
  const [weexKey, setWeexKey] = useState("");
  const [weexSecret, setWeexSecret] = useState("");
  const [weexPass, setWeexPass] = useState("");
  const [tgToken, setTgToken] = useState("");
  const [tgChat, setTgChat] = useState("");

  const [saving, setSaving] = useState(false);
  const [testingTg, setTestingTg] = useState(false);

  // Hydrate draft from saved when first loaded / changed externally
  useEffect(() => {
    if (saved && Object.keys(draft).length === 0) {
      setDraft({
        mode: saved.mode || "balanced",
        scan_interval_minutes: saved.scan_interval_minutes ?? 5,
        min_signal_score: saved.min_signal_score ?? 75,
        max_pairs: saved.max_pairs ?? 50,
        risk_percent: saved.risk_percent ?? 1.0,
        enabled_strategies: saved.enabled_strategies || [
          "trend_pullback",
          "breakout_retest",
          "range_trading",
          "liquidity_sweep",
        ],
        enable_liquidity_sweep: saved.enable_liquidity_sweep ?? false,
        enable_range_trading: saved.enable_range_trading ?? false,
        enable_ma_crossover: saved.enable_ma_crossover ?? false,
        enable_divergence: saved.enable_divergence ?? false,
        enable_fibonacci_retracement: saved.enable_fibonacci_retracement ?? false,
        enable_ichimoku: saved.enable_ichimoku ?? false,
        enable_volume_profile: saved.enable_volume_profile ?? false,
        enable_chart_patterns: saved.enable_chart_patterns ?? false,
        enable_momentum_rotation: saved.enable_momentum_rotation ?? false,
        thesis_invalidation_enabled: saved.thesis_invalidation_enabled ?? true,
        funding_soft_threshold: saved.funding_soft_threshold ?? "",
        funding_hard_threshold: saved.funding_hard_threshold ?? "",
        oi_soft_pct: saved.oi_soft_pct ?? "",
        oi_hard_pct: saved.oi_hard_pct ?? "",
        trending_up_long_min_adx: saved.trending_up_long_min_adx ?? "",
        trend_pullback_min_volume_ratio: saved.trend_pullback_min_volume_ratio ?? "",
        paper_trading_enabled: saved.paper_trading_enabled ?? true,
        paper_starting_balance: saved.paper_starting_balance ?? 10000,
        paper_risk_percent: saved.paper_risk_percent ?? 2.0,
        paper_leverage_majors: saved.paper_leverage_majors ?? 100,
        paper_leverage_default: saved.paper_leverage_default ?? 15,
        telegram_chat_id_visible: saved.telegram_chat_id || "",
      });
    }
  }, [saved, draft]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const toggleStrategy = (s) => {
    const list = new Set(draft.enabled_strategies || []);
    if (list.has(s)) list.delete(s);
    else list.add(s);
    set("enabled_strategies", Array.from(list));
  };

  const cancelWeex = () => {
    setEditWeex(false);
    setWeexKey("");
    setWeexSecret("");
    setWeexPass("");
  };
  const cancelTelegram = () => {
    setEditTelegram(false);
    setTgToken("");
    setTgChat("");
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = {
        mode: draft.mode,
        scan_interval_minutes: Number(draft.scan_interval_minutes) || 5,
        min_signal_score: Number(draft.min_signal_score) || 75,
        max_pairs: Number(draft.max_pairs) || 50,
        risk_percent: Number(draft.risk_percent) || 1.0,
        enabled_strategies: draft.enabled_strategies,
        enable_liquidity_sweep: !!draft.enable_liquidity_sweep,
        enable_range_trading: !!draft.enable_range_trading,
        enable_ma_crossover: !!draft.enable_ma_crossover,
        enable_divergence: !!draft.enable_divergence,
        enable_fibonacci_retracement: !!draft.enable_fibonacci_retracement,
        enable_ichimoku: !!draft.enable_ichimoku,
        enable_volume_profile: !!draft.enable_volume_profile,
        enable_chart_patterns: !!draft.enable_chart_patterns,
        enable_momentum_rotation: !!draft.enable_momentum_rotation,
        thesis_invalidation_enabled: !!draft.thesis_invalidation_enabled,
        // OI / funding overrides — submit null when blank so the backend
        // falls back to locked defaults (0.0005 / 0.003 / 10.0 / 30.0).
        funding_soft_threshold: blankToNull(draft.funding_soft_threshold),
        funding_hard_threshold: blankToNull(draft.funding_hard_threshold),
        oi_soft_pct: blankToNull(draft.oi_soft_pct),
        oi_hard_pct: blankToNull(draft.oi_hard_pct),
        // Phase 2.5 surgical filter — min 4H ADX for TRENDING_UP longs (default 40).
        trending_up_long_min_adx: blankToNull(draft.trending_up_long_min_adx),
        // Post-deploy SHORT fix — min 15m volume ratio for trend_pullback shorts (default 1.5).
        trend_pullback_min_volume_ratio: blankToNull(draft.trend_pullback_min_volume_ratio),
        // Phase 3 — paper trading config
        paper_trading_enabled: !!draft.paper_trading_enabled,
        paper_starting_balance: Number(draft.paper_starting_balance) || 10000,
        paper_risk_percent: Number(draft.paper_risk_percent) || 2.0,
        paper_leverage_majors: Number(draft.paper_leverage_majors) || 100,
        paper_leverage_default: Number(draft.paper_leverage_default) || 15,
      };
      if (editWeex) {
        if (weexKey) payload.weex_api_key = weexKey;
        if (weexSecret) payload.weex_api_secret = weexSecret;
        if (weexPass) payload.weex_passphrase = weexPass;
      }
      if (editTelegram) {
        if (tgToken) payload.telegram_bot_token = tgToken;
        if (tgChat) payload.telegram_chat_id = tgChat;
      }
      await updateSettings(payload);
      await mutate();
      cancelWeex();
      cancelTelegram();
      toast.success("Settings saved", {
        description: "Engine configuration updated.",
      });
    } catch (e) {
      toast.error("Failed to save settings", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const onTestTelegram = async () => {
    // Prefer drafts (if editing) else saved
    const token = editTelegram ? tgToken : saved?.telegram_bot_token;
    const chat = editTelegram ? tgChat : saved?.telegram_chat_id;
    if (!token || !chat) {
      toast.error("Telegram not configured", {
        description: "Provide bot token and chat ID before testing.",
      });
      return;
    }
    setTestingTg(true);
    try {
      const res = await testTelegram(token, chat);
      if (res?.ok) {
        toast.success("Telegram test sent", {
          description: "Check your chat for the test message.",
        });
      } else {
        toast.error("Telegram test failed", {
          description: res?.error || "Unknown error",
        });
      }
    } catch (e) {
      toast.error("Telegram test failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setTestingTg(false);
    }
  };

  if (!saved || Object.keys(draft).length === 0) {
    return (
      <div className="text-xs text-slate-500 font-data">Loading settings…</div>
    );
  }

  return (
    <div className="flex flex-col gap-6 max-w-[1200px]">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-slate-500 font-bold">
            Engine Config
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-black tracking-tighter text-slate-900 mt-1">
            Settings
          </h1>
          <div className="text-xs text-slate-500 mt-2">
            Configure API keys, Telegram delivery, scanner cadence, risk and strategies.
          </div>
        </div>
        <button
          data-testid={SETTINGS.saveBtn}
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#2563eb]/90 disabled:opacity-50 text-white text-xs font-bold tracking-widest uppercase px-4 py-2.5 rounded-sm transition"
        >
          <FloppyDisk size={14} weight="bold" />
          {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>

      {/* Engine Mode + Cadence + Risk */}
      <Panel>
        <SectionHeader
          icon={Lightning}
          title="Engine"
          hint="Mode preset, scan cadence, score threshold and risk per trade."
        />
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4">
          <div>
            <Label hint="Risk profile & defaults">Mode</Label>
            <Select
              testid={SETTINGS.modeSelect}
              value={draft.mode}
              onChange={(v) => set("mode", v)}
              options={MODES.map((m) => ({ v: m.v, l: m.l }))}
            />
          </div>
          <div>
            <Label hint="Minutes between scans">Interval (min)</Label>
            <NumberInput
              testid={SETTINGS.intervalInput}
              value={draft.scan_interval_minutes}
              onChange={(v) => set("scan_interval_minutes", v)}
              min={1}
              max={60}
              step={1}
            />
          </div>
          <div>
            <Label hint="0–100. Lower = more signals">Min Score</Label>
            <NumberInput
              testid={SETTINGS.minScoreInput}
              value={draft.min_signal_score}
              onChange={(v) => set("min_signal_score", v)}
              min={0}
              max={100}
              step={1}
            />
          </div>
          <div>
            <Label hint="Top N pairs per scan">Max Pairs</Label>
            <NumberInput
              testid={SETTINGS.maxPairsInput}
              value={draft.max_pairs}
              onChange={(v) => set("max_pairs", v)}
              min={5}
              max={200}
              step={1}
            />
          </div>
          <div>
            <Label hint="% of equity per trade">Risk %</Label>
            <NumberInput
              testid={SETTINGS.riskInput}
              value={draft.risk_percent}
              onChange={(v) => set("risk_percent", v)}
              min={0.1}
              max={10}
              step={0.1}
            />
          </div>
        </div>
      </Panel>

      {/* OI / Funding Filters — Phase 2 */}
      <Panel data-testid="settings-filters-panel">
        <SectionHeader
          icon={Funnel}
          title="Open Interest & Funding Filters"
          hint="Soft penalty first, hard reject only at extremes. Leave blank to use locked defaults."
        />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4">
          <div>
            <Label hint={`Default ${FILTER_DEFAULTS.funding_soft_threshold} (0.05%)`}>
              Funding · Soft
            </Label>
            <NumberInput
              testid={SETTINGS.fundingSoftInput}
              value={draft.funding_soft_threshold}
              onChange={(v) => set("funding_soft_threshold", v)}
              placeholder={String(FILTER_DEFAULTS.funding_soft_threshold)}
              min={0}
              step={0.0001}
            />
          </div>
          <div>
            <Label hint={`Default ${FILTER_DEFAULTS.funding_hard_threshold} (0.30%)`}>
              Funding · Hard
            </Label>
            <NumberInput
              testid={SETTINGS.fundingHardInput}
              value={draft.funding_hard_threshold}
              onChange={(v) => set("funding_hard_threshold", v)}
              placeholder={String(FILTER_DEFAULTS.funding_hard_threshold)}
              min={0}
              step={0.0001}
            />
          </div>
          <div>
            <Label hint={`Default ${FILTER_DEFAULTS.oi_soft_pct}% (1h Δ)`}>
              OI · Soft %
            </Label>
            <NumberInput
              testid={SETTINGS.oiSoftInput}
              value={draft.oi_soft_pct}
              onChange={(v) => set("oi_soft_pct", v)}
              placeholder={String(FILTER_DEFAULTS.oi_soft_pct)}
              min={0}
              step={0.5}
            />
          </div>
          <div>
            <Label hint={`Default ${FILTER_DEFAULTS.oi_hard_pct}% (1h Δ)`}>
              OI · Hard %
            </Label>
            <NumberInput
              testid={SETTINGS.oiHardInput}
              value={draft.oi_hard_pct}
              onChange={(v) => set("oi_hard_pct", v)}
              placeholder={String(FILTER_DEFAULTS.oi_hard_pct)}
              min={0}
              step={0.5}
            />
          </div>
        </div>
      </Panel>

      {/* Trend Entry Filter — Phase 2.5 (surgical) */}
      <Panel data-testid="settings-trend-filter-panel">
        <SectionHeader
          icon={TrendUp}
          title="Trend Entry Filter"
          hint="Surgical entry gates. ADX targets weak TRENDING_UP longs; volume gate targets weak trend_pullback shorts. Other strategies are unaffected. Leave blank to use locked defaults."
        />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4">
          <div>
            <Label hint={`Only applies to LONG signals in TRENDING_UP regime. Higher value means fewer but stronger long signals. Default ${FILTER_DEFAULTS.trending_up_long_min_adx}.`}>
              TRENDING_UP LONG minimum ADX
            </Label>
            <NumberInput
              testid={SETTINGS.trendingUpMinAdxInput}
              value={draft.trending_up_long_min_adx}
              onChange={(v) => set("trending_up_long_min_adx", v)}
              placeholder={String(FILTER_DEFAULTS.trending_up_long_min_adx)}
              min={0}
              step={1}
            />
          </div>
          <div>
            <Label hint={`Default ${FILTER_DEFAULTS.trend_pullback_min_volume_ratio} (15m vol ratio)`}>
              Trend Pullback Short · Min Volume
            </Label>
            <NumberInput
              testid={SETTINGS.trendPullbackMinVolInput}
              value={draft.trend_pullback_min_volume_ratio}
              onChange={(v) => set("trend_pullback_min_volume_ratio", v)}
              placeholder={String(FILTER_DEFAULTS.trend_pullback_min_volume_ratio)}
              min={0}
              step={0.1}
            />
          </div>
        </div>
      </Panel>

      {/* Thesis Invalidation — Phase 5 (advisory only) */}
      <Panel data-testid="settings-thesis-invalidation-panel">
        <SectionHeader
          icon={Warning}
          title="Thesis Invalidation Monitor"
          hint="Advisory only — never closes a position. Re-checks each open signal's original 4H thesis (trend, regime, ADX) once per scan cycle and flags it (+ Telegram alert) after two consecutive confirming checks if it's broken. You decide what to do."
        />
        <div className="p-4">
          <label className="flex items-center justify-between gap-3 px-3 py-3 border border-slate-200 rounded-sm bg-[#f1f5f9] cursor-pointer">
            <div>
              <span className="text-sm text-slate-900">Enable Thesis Invalidation Monitor</span>
              <div className="text-[11px] text-slate-400 mt-0.5">
                On by default — read-only, adds no trading risk. Turn off to skip the extra per-position candle checks.
              </div>
            </div>
            <input
              data-testid="settings-enable-thesis-invalidation"
              type="checkbox"
              checked={draft.thesis_invalidation_enabled ?? true}
              onChange={(e) => set("thesis_invalidation_enabled", e.target.checked)}
              className="h-4 w-4 accent-[#2563eb] shrink-0"
            />
          </label>
        </div>
      </Panel>

      {/* Paper Trading — Phase 3 */}
      <Panel data-testid="settings-paper-panel">
        <SectionHeader
          icon={Wallet}
          title="Paper Trading"
          hint="Virtual portfolio sizing. Each accepted signal auto-opens a position. No real orders are placed."
        />
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4">
          <div className="md:col-span-1">
            <Label hint="Auto-open virtual trades">Enabled</Label>
            <label className="flex items-center gap-3 px-3 py-2 border border-slate-200 rounded-sm bg-[#f1f5f9] cursor-pointer">
              <input
                data-testid={SETTINGS.paperEnabledToggle}
                type="checkbox"
                checked={!!draft.paper_trading_enabled}
                onChange={(e) => set("paper_trading_enabled", e.target.checked)}
                className="h-4 w-4 accent-[#2563eb]"
              />
              <span className="text-sm text-slate-900">
                {draft.paper_trading_enabled ? "On" : "Off"}
              </span>
            </label>
          </div>
          <div>
            <Label hint="Virtual USD">Starting Balance</Label>
            <NumberInput
              testid={SETTINGS.paperBalanceInput}
              value={draft.paper_starting_balance}
              onChange={(v) => set("paper_starting_balance", v)}
              min={100}
              step={100}
            />
          </div>
          <div>
            <Label hint="% of balance per trade">Risk %</Label>
            <NumberInput
              testid={SETTINGS.paperRiskInput}
              value={draft.paper_risk_percent}
              onChange={(v) => set("paper_risk_percent", v)}
              min={0.1}
              max={20}
              step={0.1}
            />
          </div>
          <div>
            <Label hint="BTC / ETH leverage">Leverage · Majors</Label>
            <NumberInput
              testid={SETTINGS.paperLevMajorsInput}
              value={draft.paper_leverage_majors}
              onChange={(v) => set("paper_leverage_majors", v)}
              min={1}
              max={125}
              step={1}
            />
          </div>
          <div>
            <Label hint="All other pairs">Leverage · Default</Label>
            <NumberInput
              testid={SETTINGS.paperLevDefaultInput}
              value={draft.paper_leverage_default}
              onChange={(v) => set("paper_leverage_default", v)}
              min={1}
              max={125}
              step={1}
            />
          </div>
        </div>
      </Panel>

      {/* WEEX API */}
      <Panel>
        <SectionHeader
          icon={Key}
          title="WEEX API"
          hint="Optional. Public endpoints are used when keys are blank."
          action={
            <EditToggle
              editing={editWeex}
              onToggle={() => setEditWeex(true)}
              onCancel={cancelWeex}
              testidEdit="settings-weex-edit"
              testidCancel="settings-weex-cancel"
            />
          }
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4">
          <div>
            <Label>API Key</Label>
            <SecretField
              testid={SETTINGS.weexKeyInput}
              savedValue={saved.weex_api_key}
              draftValue={weexKey}
              onChangeDraft={setWeexKey}
              editing={editWeex}
              placeholder="wx_xxx…"
            />
          </div>
          <div>
            <Label>API Secret</Label>
            <SecretField
              testid={SETTINGS.weexSecretInput}
              savedValue={saved.weex_api_secret}
              draftValue={weexSecret}
              onChangeDraft={setWeexSecret}
              editing={editWeex}
              placeholder="secret"
            />
          </div>
          <div>
            <Label>Passphrase</Label>
            <SecretField
              testid={SETTINGS.weexPassInput}
              savedValue={saved.weex_passphrase}
              draftValue={weexPass}
              onChangeDraft={setWeexPass}
              editing={editWeex}
              placeholder="passphrase"
            />
          </div>
        </div>
      </Panel>

      {/* Telegram */}
      <Panel>
        <SectionHeader
          icon={PaperPlaneTilt}
          title="Telegram Alerts"
          hint="Bot token and chat ID for signal delivery."
          action={
            <div className="flex items-center gap-2">
              <button
                data-testid={SETTINGS.testTgBtn}
                onClick={onTestTelegram}
                disabled={testingTg}
                className="inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase text-[#16a34a] hover:text-slate-900 border border-[#16a34a]/30 hover:border-[#16a34a] px-2.5 py-1 rounded-sm transition disabled:opacity-50"
              >
                <PaperPlaneTilt size={11} weight="bold" />
                {testingTg ? "Sending…" : "Send Test"}
              </button>
              <EditToggle
                editing={editTelegram}
                onToggle={() => setEditTelegram(true)}
                onCancel={cancelTelegram}
                testidEdit="settings-telegram-edit"
                testidCancel="settings-telegram-cancel"
              />
            </div>
          }
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
          <div>
            <Label hint="From @BotFather">Bot Token</Label>
            <SecretField
              testid={SETTINGS.tgTokenInput}
              savedValue={saved.telegram_bot_token}
              draftValue={tgToken}
              onChangeDraft={setTgToken}
              editing={editTelegram}
              placeholder="123456:ABC-…"
            />
          </div>
          <div>
            <Label hint="User, channel or group ID">Chat ID</Label>
            <SecretField
              testid={SETTINGS.tgChatInput}
              savedValue={saved.telegram_chat_id}
              draftValue={tgChat}
              onChangeDraft={setTgChat}
              editing={editTelegram}
              placeholder="-100…"
            />
          </div>
        </div>
      </Panel>

      {/* Strategies */}
      <Panel>
        <SectionHeader
          icon={ShieldCheck}
          title="Active Strategies"
          hint="Core strategies run when checked. Everything below is a gated strategy, each controlled by its own switch — off by default, enable one at a time and watch it in the paper account."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4">
          {STRATEGIES.map((s) => {
            const on = (draft.enabled_strategies || []).includes(s.v);
            return (
              <label
                key={s.v}
                className={`flex items-center justify-between gap-3 px-3 py-3 border rounded-sm cursor-pointer transition ${
                  on
                    ? "border-[#2563eb]/40 bg-[#2563eb]/5"
                    : "border-slate-200 hover:border-slate-300 bg-[#f1f5f9]"
                }`}
              >
                <span className="text-sm text-slate-900">{s.l}</span>
                <input
                  data-testid={SETTINGS.strategyCheckbox(s.v)}
                  type="checkbox"
                  checked={on}
                  onChange={() => toggleStrategy(s.v)}
                  className="h-4 w-4 accent-[#2563eb]"
                />
              </label>
            );
          })}
        </div>
        <div className="px-4 pb-4 flex flex-col gap-2">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 pt-1">
            Gated Strategies
          </div>
          {GATED_STRATEGIES.map((g) => (
            <label
              key={g.key}
              className="flex items-center justify-between gap-3 px-3 py-3 border border-slate-200 rounded-sm bg-[#f1f5f9] cursor-pointer"
            >
              <div>
                <span className="text-sm text-slate-900">{g.label}</span>
                <div className="text-[11px] text-slate-400 mt-0.5">{g.note}</div>
              </div>
              <input
                data-testid={g.testid}
                type="checkbox"
                checked={!!draft[g.key]}
                onChange={(e) => set(g.key, e.target.checked)}
                className="h-4 w-4 accent-[#2563eb] shrink-0"
              />
            </label>
          ))}
        </div>
      </Panel>

      <div className="flex justify-end">
        <button
          data-testid="settings-save-btn-bottom"
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#2563eb]/90 disabled:opacity-50 text-white text-xs font-bold tracking-widest uppercase px-4 py-2.5 rounded-sm transition"
        >
          <FloppyDisk size={14} weight="bold" />
          {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>
    </div>
  );
};

export default Settings;
